import { AiMessageSteps } from "__generated__/graphql"

import { asRecord, parseJsonIfPossible } from "./aiStepsDrawer.utils"
import { toolLabel } from "./v2AgentLoop.labels"
import { isV2Run } from "./v2RunOverview.utils"

/**
 * What one turn drew on — guidance, skills, knowledge, actions, store tools and the AI profile —
 * each with the steps it was used in, so "where did this come from" is one list instead of a
 * walk through every raw step.
 */

export type SourceKind = "guidance" | "skill" | "knowledge" | "action" | "mcp"

export type SourceUse = {
  stepId: string
  stepNo: number | null
  where: string
  /** The model's own words for that use — the query it searched, the argument it passed. */
  note: string
  failed: boolean
}

export type SourceItem = {
  key: string
  name: string
  detail: string
  /** Full text when the step carried it (v1 guidance content, a knowledge excerpt). */
  text: string
  uses: SourceUse[]
}

export type ProfileSnapshot = {
  stepId: string
  stepNo: number | null
  role: string | null
  name: string | null
  persona: string | null
  toneOfVoice: string | null
  answerLength: string | null
  useEmoji: boolean
  introduction: string | null
  handoffDisabledMessage: string | null
  learnFromHumanReplies: boolean
  useMacrosAsKnowledgeSource: boolean
}

export type PromptDecision = { flag: string; on: boolean }

export type UsedSources = {
  role: {
    role: string
    reason: string
    stepId: string
    stepNo: number | null
  } | null
  profile: ProfileSnapshot | null
  decisions: {
    stepId: string
    stepNo: number | null
    flags: PromptDecision[]
  } | null
  groups: Record<SourceKind, SourceItem[]>
  droppedGuidance: SourceItem[]
  /** v2 runs written before the rows were recorded carry only the count. */
  guidanceCountOnly: number | null
}

const asText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : ""

const asList = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : []

const stepName = (step: AiMessageSteps): string => String(step.step ?? "")

const payload = (value: unknown) => asRecord(parseJsonIfPossible(value))

const isFailed = (step: AiMessageSteps): boolean =>
  String(step.status ?? "") === "FAILED"

export const GUIDANCE_CATEGORY_LABELS: Record<string, string> = {
  COMMUNICATION_STYLE: "İletişim tarzı",
  CONTEXT_AND_CLARIFICATION: "Bağlam ve netleştirme",
  HANDOVER_AND_ESCALATION: "İnsana devir",
  SPAM: "Spam",
  OTHERS: "Diğer"
}

const STEP_WHERE_LABELS: Record<string, string> = {
  AGENT_CONTEXT: "Ajan promptu",
  GUIDANCE_SELECTION: "Guidance seçimi",
  SKILL_ROUTING: "Skill yönlendirme",
  SKILL_AGENT: "Skill ajanı",
  RERANK_CHUNKS: "Bilgi parçası sıralama",
  FUSE_RAG_RESULTS: "Bilgi arama sonuçları",
  GET_PINECONE_DATA: "Vektör arama",
  ACTION_DETECTION: "Action seçimi",
  HTTP_REQUEST: "HTTP isteği",
  COMMERCE_MCP_TOOL_CALL: "Mağaza aracı",
  GET_QPIEN_SHOP_PRODUCT_DETAILS: "Qpien Shop ürün okuma",
  GET_QPIEN_SHOP_ORDER_DETAILS: "Qpien Shop sipariş okuma",
  MARKETPLACE_REQUEST: "Pazaryeri API"
}

const SKILL_TOOL_PREFIX = "skill_"
const CHANNEL_ACTION_PREFIX = "action_"
const HOST_READ_STEPS: Record<string, string> = {
  GET_QPIEN_SHOP_PRODUCT_DETAILS: "Qpien Shop ürün bilgisi",
  GET_QPIEN_SHOP_ORDER_DETAILS: "Qpien Shop sipariş bilgisi",
  MARKETPLACE_REQUEST: "Pazaryeri API ürün bilgisi"
}

const TURKISH_MAP: Record<string, string> = {
  ç: "c",
  ğ: "g",
  ı: "i",
  ö: "o",
  ş: "s",
  ü: "u",
  â: "a",
  î: "i",
  û: "u"
}

/** Mirrors qpien-ai's `channelActionToolName` minus the id suffix, to name an `action_*` tool. */
const actionSlug = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[çğıöşüâîû]/g, (char) => TURKISH_MAP[char] ?? char)
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40)

const NOTE_KEYS = [
  "query",
  "reason",
  "search",
  "productId",
  "orderId",
  "referenceId"
]

function pickNote(args: Record<string, unknown> | null): string {
  if (!args) return ""
  for (const key of NOTE_KEYS) {
    const value = asText(args[key])
    if (value) return value
  }
  return ""
}

function urlLabel(request: Record<string, unknown> | null): string {
  const url = asText(request?.url)
  let host = url
  try {
    host = new URL(url).host
  } catch {
    /* relative or templated url — shown as written */
  }
  return [asText(request?.method), host].filter(Boolean).join(" ")
}

class Registry {
  private readonly items = new Map<string, SourceItem>()

  add(
    key: string,
    name: string,
    use: SourceUse | null,
    extra: { detail?: string; text?: string } = {}
  ) {
    const existing = this.items.get(key)
    const item = existing ?? {
      key,
      name,
      detail: extra.detail ?? "",
      text: extra.text ?? "",
      uses: []
    }
    if (!item.detail && extra.detail) item.detail = extra.detail
    if (!item.text && extra.text) item.text = extra.text
    if (use && !item.uses.some((u) => u.stepId === use.stepId)) {
      item.uses.push(use)
    }
    this.items.set(key, item)
  }

  list(): SourceItem[] {
    return Array.from(this.items.values())
  }
}

type RagMetadata = { id: string; name: string; topic: string; text: string }

function readRagMetadata(
  step: AiMessageSteps | undefined
): Map<string, RagMetadata> {
  const map = new Map<string, RagMetadata>()
  const response = payload(step?.response)
  const rows = [...asList(response?.results), ...asList(response?.matches)]
  rows.forEach((entry) => {
    const row = asRecord(entry)
    const metadata = asRecord(row?.metadata)
    const chunkId = asText(row?.id)
    const sourceId = asText(metadata?.aiKnowledgeSourceId)
    if (!chunkId || !sourceId) return
    map.set(chunkId, {
      id: sourceId,
      name: asText(metadata?.subTopic) || asText(metadata?.topic) || sourceId,
      topic: asText(metadata?.topic),
      text: asText(row?.content) || asText(metadata?.content)
    })
  })
  return map
}

function readRerankIds(step: AiMessageSteps): string[] {
  const raw = parseJsonIfPossible(step.response)
  const record = asRecord(raw)
  const inner = record?.text ? asRecord(parseJsonIfPossible(record.text)) : null
  const chunks = asList((inner ?? record)?.chunks)
  return chunks.map((chunk) => asText(asRecord(chunk)?.id)).filter(Boolean)
}

function readProfile(
  step: AiMessageSteps,
  request: Record<string, unknown> | null
): ProfileSnapshot | null {
  const profile = asRecord(request?.profile)
  if (!profile) return null
  return {
    stepId: String(step._id),
    stepNo: step.stepList ?? null,
    role: asText(profile.role) || asText(request?.role) || null,
    name: asText(profile.name) || null,
    persona: asText(profile.persona) || null,
    toneOfVoice: asText(profile.toneOfVoice) || null,
    answerLength: asText(profile.answerLength) || null,
    useEmoji: profile.useEmoji === true,
    introduction: asText(profile.introduction) || null,
    handoffDisabledMessage: asText(profile.handoffDisabledMessage) || null,
    learnFromHumanReplies: profile.learnFromHumanReplies === true,
    useMacrosAsKnowledgeSource: profile.useMacrosAsKnowledgeSource === true
  }
}

function readDecisions(
  request: Record<string, unknown> | null
): PromptDecision[] {
  const flags = asRecord(request?.promptFlags)
  if (!flags) return []
  return Object.entries(flags).map(([flag, value]) => ({
    flag,
    on: value === true
  }))
}

export function buildUsedSources(
  steps: AiMessageSteps[] | undefined | null
): UsedSources {
  const list = [...(steps ?? [])].sort(
    (a, b) => (a.stepList ?? 0) - (b.stepList ?? 0)
  )

  const guidance = new Registry()
  const dropped = new Registry()
  const skills = new Registry()
  const knowledge = new Registry()
  const actions = new Registry()
  const mcp = new Registry()

  const result: UsedSources = {
    role: null,
    profile: null,
    decisions: null,
    groups: {
      guidance: [],
      skill: [],
      knowledge: [],
      action: [],
      mcp: []
    },
    droppedGuidance: [],
    guidanceCountOnly: null
  }

  const actionNamesBySlug = new Map<string, string>()
  const v1ActionNames = new Map<string, string>()
  let round = 0
  let parentTool = ""
  let activeSkill: { key: string; name: string } | null = null
  let lastDetectedAction = ""
  let lastRagMetadata = new Map<string, RagMetadata>()
  const hasRerank = list.some((step) => stepName(step) === "RERANK_CHUNKS")
  const v2 = isV2Run(list)

  const usageAt = (
    step: AiMessageSteps,
    where: string,
    note = ""
  ): SourceUse => ({
    stepId: String(step._id),
    stepNo: step.stepList ?? null,
    where,
    note,
    failed: isFailed(step)
  })

  const whereOf = (step: AiMessageSteps): string => {
    const name = stepName(step)
    const label = STEP_WHERE_LABELS[name] ?? name
    if (round === 0) return label
    return parentTool
      ? `Tur ${round} · ${parentTool} · ${label}`
      : `Tur ${round} · ${label}`
  }

  const addRag = (
    step: AiMessageSteps,
    metadata: RagMetadata | undefined,
    note = ""
  ) => {
    if (!metadata) return
    knowledge.add(
      metadata.id,
      metadata.name,
      usageAt(step, whereOf(step), note),
      {
        detail: metadata.topic !== metadata.name ? metadata.topic : "",
        text: metadata.text
      }
    )
  }

  const handleToolCall = (
    step: AiMessageSteps,
    request: Record<string, unknown> | null,
    response: Record<string, unknown> | null
  ) => {
    const tool = asText(request?.tool) || "tool"
    const args = asRecord(parseJsonIfPossible(request?.arguments))
    const label = toolLabel(tool)
    const where = `Tur ${Math.max(round, 1)} · ${label}`
    parentTool = label

    if (tool === "search_knowledge") {
      const query = asText(args?.query) || asText(response?.query)
      asList(response?.results).forEach((entry, position) => {
        const hit = asRecord(entry)
        const title = asText(hit?.title)
        const id =
          asText(hit?.sourceId) ||
          asText(hit?.id) ||
          title ||
          `${String(step._id)}-${position}`
        knowledge.add(
          id,
          title || "Başlıksız bilgi parçası",
          usageAt(step, where, query),
          {
            text: asText(hit?.excerpt)
          }
        )
      })
      return
    }

    if (tool === "skill_knowledge") {
      const source = asText(response?.source)
      const found = asList(response?.chunks).length
      if (found || source) {
        knowledge.add(
          `skill-source:${source || "skill"}`,
          source || "Skill bilgi kaynağı",
          usageAt(step, where, asText(args?.query)),
          { detail: "Skill içinden" }
        )
      }
    }

    if (tool === "start_skill") {
      const skill = asRecord(response?.skill)
      const skillName = asText(skill?.name)
      if (skillName) {
        activeSkill = {
          key: asText(skill?.id) || `name:${skillName}`,
          name: skillName
        }
      }
    }

    if (tool.startsWith(SKILL_TOOL_PREFIX) || tool === "start_skill") {
      if (activeSkill) {
        skills.add(
          activeSkill.key,
          activeSkill.name,
          usageAt(step, where, pickNote(args))
        )
      }
      return
    }

    if (tool.startsWith(CHANNEL_ACTION_PREFIX)) {
      const slug = tool
        .slice(CHANNEL_ACTION_PREFIX.length)
        .replace(/_[^_]+$/, "")
      const actionName = actionNamesBySlug.get(slug) ?? tool
      actions.add(
        `name:${actionName}`,
        actionName,
        usageAt(step, where, pickNote(args))
      )
      return
    }

    if (tool !== "find_skill") {
      mcp.add(`tool:${tool}`, label, usageAt(step, where, pickNote(args)), {
        detail: tool
      })
    }
  }

  for (const step of list) {
    const name = stepName(step)
    const request = payload(step.request)
    const response = payload(step.response)

    switch (name) {
      case "ROLE_SELECTION": {
        const role = asText(response?.role)
        if (role) {
          result.role = {
            role,
            reason: asText(response?.reason),
            stepId: String(step._id),
            stepNo: step.stepList ?? null
          }
        }
        break
      }
      case "AI_ROLE_RESOLUTION": {
        const role = asText(response?.resolvedRole)
        if (role) {
          result.role = {
            role,
            reason: asText(response?.decision),
            stepId: String(step._id),
            stepNo: step.stepList ?? null
          }
        }
        break
      }
      case "AGENT_CONTEXT": {
        result.profile = readProfile(step, request)
        const flags = readDecisions(request)
        if (flags.length) {
          result.decisions = {
            stepId: String(step._id),
            stepNo: step.stepList ?? null,
            flags
          }
        }
        const block = asRecord(request?.guidance)
        const rows = asList(block?.rows)
        if (block && !Array.isArray(block.rows)) {
          result.guidanceCountOnly =
            typeof block.total === "number" ? block.total : null
        }
        rows.forEach((entry) => {
          const row = asRecord(entry)
          const id = asText(row?.id)
          if (!id) return
          guidance.add(
            id,
            asText(row?.title) || id,
            usageAt(step, "Ajan promptu"),
            {
              detail: GUIDANCE_CATEGORY_LABELS[asText(row?.category)] ?? ""
            }
          )
        })
        asList(block?.droppedRows).forEach((entry) => {
          const row = asRecord(entry)
          const id = asText(row?.id)
          if (!id) return
          dropped.add(
            id,
            asText(row?.title) || id,
            usageAt(step, "Ajan promptu"),
            {
              detail: GUIDANCE_CATEGORY_LABELS[asText(row?.category)] ?? ""
            }
          )
        })
        asList(request?.channelActions).forEach((entry) => {
          const actionName = asText(entry)
          if (actionName)
            actionNamesBySlug.set(actionSlug(actionName), actionName)
        })
        const skillBlock = asRecord(request?.skills)
        const active = asText(skillBlock?.active)
        if (active) activeSkill = { key: `name:${active}`, name: active }
        break
      }
      case "AGENT_MODEL_CALL":
        round += 1
        parentTool = ""
        break
      case "AGENT_TOOL_CALL":
        handleToolCall(step, request, response)
        break
      case "GUIDANCE_SELECTION": {
        const messageType = asText(request?.messageType)
        const where = messageType
          ? `Guidance seçimi · ${messageType}`
          : "Guidance seçimi"
        Object.entries(response ?? {}).forEach(([category, rows]) => {
          asList(rows).forEach((entry) => {
            const row = asRecord(entry)
            const content = asText(row?.content)
            const id = asText(row?._id) || content
            if (!id) return
            guidance.add(
              id,
              asText(row?.title) ||
                GUIDANCE_CATEGORY_LABELS[category] ||
                category,
              usageAt(step, where),
              {
                detail: GUIDANCE_CATEGORY_LABELS[category] ?? category,
                text: content
              }
            )
          })
        })
        break
      }
      case "SKILL_ROUTING": {
        if (asText(response?.route) !== "ENTERED") break
        const skillName = asText(response?.skillName)
        if (!skillName) break
        activeSkill = {
          key: asText(response?.skillId) || `name:${skillName}`,
          name: skillName
        }
        skills.add(
          activeSkill.key,
          activeSkill.name,
          usageAt(step, whereOf(step))
        )
        break
      }
      case "SKILL_AGENT":
        if (activeSkill) {
          skills.add(
            activeSkill.key,
            activeSkill.name,
            usageAt(step, whereOf(step), asText(response?.command))
          )
        }
        break
      case "GET_PINECONE_DATA":
      case "FUSE_RAG_RESULTS": {
        // v2's RAG steps hold what was retrieved; what the agent saw is the search_knowledge reply.
        if (v2) break
        const metadata = readRagMetadata(step)
        if (metadata.size) lastRagMetadata = metadata
        if (!hasRerank && name === "FUSE_RAG_RESULTS") {
          metadata.forEach((meta) => addRag(step, meta))
        }
        break
      }
      case "RERANK_CHUNKS":
        if (v2) break
        readRerankIds(step).forEach((id) =>
          addRag(step, lastRagMetadata.get(id))
        )
        break
      case "GET_ACTIONS_INFO":
        asList(response?.info).forEach((entry) => {
          const info = asRecord(entry)
          const id = asText(info?.id)
          if (id && asText(info?.name))
            v1ActionNames.set(id, asText(info?.name))
        })
        break
      case "ACTION_DETECTION": {
        const actionId = asText(response?.actionId)
        if (actionId) lastDetectedAction = actionId
        break
      }
      case "HTTP_REQUEST": {
        const actionName =
          asText(step.source) ||
          v1ActionNames.get(lastDetectedAction) ||
          urlLabel(request) ||
          "HTTP isteği"
        actions.add(
          `name:${actionName}`,
          actionName,
          usageAt(step, whereOf(step)),
          {
            detail: urlLabel(request)
          }
        )
        break
      }
      case "COMMERCE_MCP_TOOL_CALL": {
        const tool = asText(request?.name) || asText(request?.tool) || "araç"
        mcp.add(
          `tool:${tool}`,
          toolLabel(tool),
          usageAt(
            step,
            v2 && round === 0 ? "Ajan başlamadan ön okuma" : whereOf(step),
            pickNote(asRecord(request?.arguments) ?? asRecord(request?.args))
          ),
          { detail: tool }
        )
        break
      }
      default:
        if (HOST_READ_STEPS[name]) {
          mcp.add(
            `host:${name}`,
            HOST_READ_STEPS[name],
            usageAt(step, whereOf(step))
          )
        }
    }
  }

  result.groups = {
    guidance: guidance.list(),
    skill: skills.list(),
    knowledge: knowledge.list(),
    action: actions.list(),
    mcp: mcp.list()
  }
  result.droppedGuidance = dropped.list()
  return result
}

export function hasUsedSources(sources: UsedSources): boolean {
  return (
    !!sources.profile ||
    !!sources.decisions ||
    sources.droppedGuidance.length > 0 ||
    Object.values(sources.groups).some((items) => items.length > 0)
  )
}
